import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { Toaster } from './components/ui/sonner';
import CookieBanner from './components/CookieBanner';
import './index.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Orders from './pages/Orders';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import KnowledgeBase from './pages/KnowledgeBase';
import Article from './pages/Article';
import Support from './pages/Support';
import MyServices from './pages/MyServices';
import Profile from './pages/Profile';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminDashboard from './pages/admin/Dashboard';
import AdminClients from './pages/admin/Clients';
import AdminClientDetail from './pages/admin/ClientDetail';
import AdminTeam from './pages/admin/Team';
import AdminServices from './pages/admin/Services';
import AdminOrders from './pages/admin/Orders';
import AdminKB from './pages/admin/KnowledgeBase';
import AdminNotifications from './pages/admin/Notifications';
import AdminIntegrations from './pages/admin/Integrations';
import AdminSiteSettings from './pages/admin/SiteSettings';
import AdminTenantSettings from './pages/admin/TenantSettings';
import AdminAISettings from './pages/admin/AISettings';
import AdminEmailSettings from './pages/admin/EmailSettings';
import BlogManager from './pages/admin/BlogManager';

// Public Pages
import LandingPage from './pages/LandingPage';
import ServiceDetailPublic from './pages/ServiceDetailPublic';
import ServicesPublic from './pages/ServicesPublic';
import VerifyTeam from './pages/VerifyTeam';
import AppSumoLanding from './pages/AppSumoLanding';

// Master Admin (Platform Level)
import MasterDashboard from './pages/master/Dashboard';

// Marketing Pages (for edrano.my)
import MarketingHome from './pages/marketing/Home';
import MarketingFeatures from './pages/marketing/Features';
import MarketingPricing from './pages/marketing/Pricing';
import MarketingUseCases from './pages/marketing/UseCases';
import MarketingDocs from './pages/marketing/Docs';
import MarketingDocArticle from './pages/marketing/DocArticle';
import MarketingCompare from './pages/marketing/Compare';
import MarketingPrivacy from './pages/marketing/Privacy';
import MarketingTerms from './pages/marketing/Terms';
import MarketingGDPR from './pages/marketing/GDPR';
import MarketingCookies from './pages/marketing/Cookies';

// Tenant Portal (White-label client portals)
import TenantPortal from './pages/portal/TenantPortal';

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Marketing Website (edrano.my) */}
      <Route path="/" element={<MarketingHome />} />
      <Route path="/features" element={<MarketingFeatures />} />
      <Route path="/pricing" element={<MarketingPricing />} />
      <Route path="/use-cases" element={<MarketingUseCases />} />
      <Route path="/compare" element={<MarketingCompare />} />
      <Route path="/docs" element={<MarketingDocs />} />
      <Route path="/docs/:slug" element={<MarketingDocArticle />} />
      <Route path="/privacy" element={<MarketingPrivacy />} />
      <Route path="/terms" element={<MarketingTerms />} />
      <Route path="/gdpr" element={<MarketingGDPR />} />
      <Route path="/cookies" element={<MarketingCookies />} />
      
      {/* Legacy marketing routes (redirect) */}
      <Route path="/marketing" element={<MarketingHome />} />
      <Route path="/marketing/*" element={<Navigate to="/" replace />} />
      
      {/* Public Service Catalog */}
      <Route path="/services" element={<ServicesPublic />} />
      <Route path="/service/:slug" element={<ServiceDetailPublic />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-team" element={<VerifyTeam />} />
      <Route path="/appsumo" element={<AppSumoLanding />} />
      
      {/* Client Routes - Authenticated */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-catalog"
        element={
          <ProtectedRoute>
            <Services />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/services"
        element={
          <ProtectedRoute>
            <Services />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-catalog/:id"
        element={
          <ProtectedRoute>
            <ServiceDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cart"
        element={
          <ProtectedRoute>
            <Cart />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <ProtectedRoute>
            <PaymentCancel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-services"
        element={
          <ProtectedRoute>
            <MyServices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base"
        element={
          <ProtectedRoute>
            <KnowledgeBase />
          </ProtectedRoute>
        }
      />
      <Route
        path="/knowledge-base/:id"
        element={
          <ProtectedRoute>
            <Article />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <Support />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute adminOnly>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <ProtectedRoute adminOnly>
            <AdminClients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/clients/:id"
        element={
          <ProtectedRoute adminOnly>
            <AdminClientDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/team"
        element={
          <ProtectedRoute adminOnly>
            <AdminTeam />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/services"
        element={
          <ProtectedRoute adminOnly>
            <AdminServices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <ProtectedRoute adminOnly>
            <AdminOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/knowledge-base"
        element={
          <ProtectedRoute adminOnly>
            <AdminKB />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/blog"
        element={
          <ProtectedRoute adminOnly>
            <BlogManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute adminOnly>
            <AdminNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/integrations"
        element={
          <ProtectedRoute adminOnly>
            <AdminIntegrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminSiteSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/portal-settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminTenantSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/ai-settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminAISettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/email-settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminEmailSettings />
          </ProtectedRoute>
        }
      />
      
      {/* Master Admin Dashboard (Platform Level - Separate Auth) */}
      <Route path="/master" element={<MasterDashboard />} />
      
      {/* Tenant Portal Routes (White-label client portals) */}
      {/* Accessed via /app/demo, /app/sociably, etc. */}
      <Route path="/app/:tenantSlug/*" element={<TenantPortal />} />
    </Routes>
  );
}

function App() {
  // Don't use basename for now - handle all routes explicitly
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" />
          <CookieBanner />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;